'use client';

import { useEffect, useRef } from 'react';
import { useTheme } from './ThemeProvider';

type TurnstileProps = {
  siteKey: string;
  onVerify: (token: string) => void;
};

/**
 * Cloudflare Turnstile component.
 */
export default function Turnstile({ siteKey, onVerify }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbackRef = useRef<(token: string) => void>(onVerify);
  const { theme: appTheme } = useTheme();

  // keep latest callback in ref to avoid re-creating the widget when parent re-renders
  useEffect(() => {
    callbackRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // If already rendered into this container, do nothing
    if (container.dataset.turnstileRendered === '1') return;

    // Load script if needed
    let script = document.getElementById('turnstile-script') as HTMLScriptElement | null;
    const attachWidget = () => {
      if (!window.turnstile) return;

      try {
        // If we have widgetId, try reset first
        if (widgetIdRef.current && typeof window.turnstile.reset === 'function') {
          try {
            window.turnstile.reset(widgetIdRef.current);
            return;
          } catch (e) {
            console.debug('turnstile.reset failed, will render new widget', e);
          }
        }

        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: appTheme === 'dark' ? 'dark' : 'light',
          callback: (token: string) => callbackRef.current?.(token),
          'error-callback': () => callbackRef.current?.(''),
          'expired-callback': () => callbackRef.current?.(''),
          'timeout-callback': () => callbackRef.current?.(''),
        });

        container.dataset.turnstileRendered = '1';
      } catch (err) {
        console.error('Turnstile render error', err);
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = attachWidget;
      script.onerror = () => console.error('Failed to load Turnstile script');
      document.head.appendChild(script);
    } else if (window.turnstile) {
      attachWidget();
    } else {
      // script exists but not loaded yet
      script.onload = attachWidget;
    }

    return () => {
      // cleanup: remove the widget if present
      try {
        if (
          widgetIdRef.current &&
          window.turnstile &&
          typeof window.turnstile.remove === 'function'
        ) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) {
        console.debug('Turnstile remove on unmount failed', e);
      }
      if (container && container.dataset) delete container.dataset.turnstileRendered;
      widgetIdRef.current = null;
    };
  }, [siteKey, appTheme]);

  return <div ref={containerRef} style={{ minHeight: 65 }} />;
}
